import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Save, AlertCircle, CheckCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const One92Settings = () => {
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [settings, setSettings] = useState({
    about_text: '',
    contact_details: {
      address: '',
      phone: '',
      email: '',
      hours: ''
    },
    social_media: {
      facebook: { url: '', enabled: false },
      twitter: { url: '', enabled: false },
      linkedin: { url: '', enabled: false },
      instagram: { url: '', enabled: false },
      youtube: { url: '', enabled: false }
    }
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/one92/settings`);
      setSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch One92 settings:', error);
      setMessage({ type: 'error', text: 'Failed to load settings' });
    }
  };

  const handleContactChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      contact_details: {
        ...prev.contact_details,
        [field]: value
      }
    }));
  };

  const handleSocialChange = (platform, field, value) => {
    setSettings(prev => ({
      ...prev,
      social_media: {
        ...prev.social_media,
        [platform]: {
          ...prev.social_media[platform],
          [field]: value
        }
      }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(
        `${API}/admin/one92/settings`,
        settings,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessage({ type: 'success', text: 'One92 settings updated successfully!' });
    } catch (error) {
      console.error('Failed to update settings:', error);
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || 'Failed to update settings' 
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-page">
      <div className="page-header">
        <h1 className="page-title">One92 Settings</h1>
        <p className="body-small" style={{ color: '#666', marginTop: '8px' }}>
          Manage contact details and social media links for the About One92 page
        </p>
      </div>

      {message.text && (
        <div className={`alert ${message.type === 'success' ? 'alert-success' : 'alert-error'}`}>
          {message.type === 'success' ? (
            <CheckCircle size={20} />
          ) : (
            <AlertCircle size={20} />
          )}
          <span>{message.text}</span>
        </div>
      )}

      <form onSubmit={handleSubmit} className="settings-form">
        {/* About Text Section */}
        <div className="form-section">
          <h3 className="heading-3" style={{ marginBottom: '16px' }}>About One92</h3>
          <div className="form-group">
            <label className="form-label">About Text</label>
            <textarea
              className="form-textarea"
              rows="6"
              value={settings.about_text}
              onChange={(e) => setSettings({ ...settings, about_text: e.target.value })}
              placeholder="Enter about text for One92..."
            />
          </div>
        </div>

        {/* Contact Details Section */}
        <div className="form-section">
          <h3 className="heading-3" style={{ marginBottom: '16px' }}>Contact Details</h3>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Address</label>
              <input
                type="text"
                className="form-input"
                value={settings.contact_details.address}
                onChange={(e) => handleContactChange('address', e.target.value)}
                placeholder="Enter address..."
              />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Phone</label>
              <input
                type="text"
                className="form-input"
                value={settings.contact_details.phone}
                onChange={(e) => handleContactChange('phone', e.target.value)}
                placeholder="Enter phone number..."
              />
            </div>
            <div className="form-group">
              <label className="form-label">Email</label>
              <input
                type="email"
                className="form-input"
                value={settings.contact_details.email}
                onChange={(e) => handleContactChange('email', e.target.value)}
                placeholder="Enter email..."
              />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Business Hours</label>
              <input
                type="text"
                className="form-input"
                value={settings.contact_details.hours}
                onChange={(e) => handleContactChange('hours', e.target.value)}
                placeholder="e.g., Mon-Sat: 10:00 AM - 7:00 PM"
              />
            </div>
          </div>
        </div>

        {/* Social Media Section */}
        <div className="form-section">
          <h3 className="heading-3" style={{ marginBottom: '16px' }}>Social Media Links</h3>
          
          {/* Facebook */}
          <div className="social-media-field">
            <div className="social-toggle-row">
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={settings.social_media.facebook.enabled}
                  onChange={(e) => handleSocialChange('facebook', 'enabled', e.target.checked)}
                />
                <span className="toggle-slider"></span>
              </label>
              <label className="form-label">Facebook</label>
            </div>
            <input
              type="url"
              className="form-input"
              value={settings.social_media.facebook.url}
              onChange={(e) => handleSocialChange('facebook', 'url', e.target.value)}
              placeholder="https://facebook.com/..."
              disabled={!settings.social_media.facebook.enabled}
            />
          </div>

          {/* Twitter */}
          <div className="social-media-field">
            <div className="social-toggle-row">
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={settings.social_media.twitter.enabled}
                  onChange={(e) => handleSocialChange('twitter', 'enabled', e.target.checked)}
                />
                <span className="toggle-slider"></span>
              </label>
              <label className="form-label">Twitter</label>
            </div>
            <input
              type="url"
              className="form-input"
              value={settings.social_media.twitter.url}
              onChange={(e) => handleSocialChange('twitter', 'url', e.target.value)}
              placeholder="https://twitter.com/..."
              disabled={!settings.social_media.twitter.enabled}
            />
          </div>

          {/* LinkedIn */}
          <div className="social-media-field">
            <div className="social-toggle-row">
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={settings.social_media.linkedin.enabled}
                  onChange={(e) => handleSocialChange('linkedin', 'enabled', e.target.checked)}
                />
                <span className="toggle-slider"></span>
              </label>
              <label className="form-label">LinkedIn</label>
            </div>
            <input
              type="url"
              className="form-input"
              value={settings.social_media.linkedin.url}
              onChange={(e) => handleSocialChange('linkedin', 'url', e.target.value)}
              placeholder="https://linkedin.com/company/..."
              disabled={!settings.social_media.linkedin.enabled}
            />
          </div>

          {/* Instagram */}
          <div className="social-media-field">
            <div className="social-toggle-row">
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={settings.social_media.instagram.enabled}
                  onChange={(e) => handleSocialChange('instagram', 'enabled', e.target.checked)}
                />
                <span className="toggle-slider"></span>
              </label>
              <label className="form-label">Instagram</label>
            </div>
            <input
              type="url"
              className="form-input"
              value={settings.social_media.instagram.url}
              onChange={(e) => handleSocialChange('instagram', 'url', e.target.value)}
              placeholder="https://instagram.com/..."
              disabled={!settings.social_media.instagram.enabled}
            />
          </div>

          {/* YouTube */}
          <div className="social-media-field">
            <div className="social-toggle-row">
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={settings.social_media.youtube.enabled}
                  onChange={(e) => handleSocialChange('youtube', 'enabled', e.target.checked)}
                />
                <span className="toggle-slider"></span>
              </label>
              <label className="form-label">YouTube</label>
            </div>
            <input
              type="url"
              className="form-input"
              value={settings.social_media.youtube.url}
              onChange={(e) => handleSocialChange('youtube', 'url', e.target.value)}
              placeholder="https://youtube.com/..."
              disabled={!settings.social_media.youtube.enabled}
            />
          </div>
        </div>

        <div className="form-actions">
          <button type="submit" className="btn-primary" disabled={loading}>
            <Save size={20} />
            <span>{loading ? 'Saving...' : 'Save Settings'}</span>
          </button>
        </div>
      </form>
    </div>
  );
};

export default One92Settings;
